import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Star, X, Check } from "lucide-react";
import { trackEvent } from "@/lib/leadTracking";
import {
  loadPreferredSourceLib,
  openPreferredSourceFlow,
  isPreferredSourceDone,
  isNudgeSnoozed,
  snoozeNudge,
  PREFERRED_SOURCE_DEEPLINK,
} from "@/lib/preferredSource";

/** Content routes where an engaged reader is most likely to opt in. */
const ELIGIBLE_PREFIXES = [
  "/",
  "/repairs",
  "/repair/",
  "/guides",
  "/info",
  "/services/",
  "/series/",
  "/city/",
  "/area/",
  "/local/",
];

const MIN_TIME_MS = 15000;
const MIN_SCROLL = 0.4;

/**
 * Subtle top-right nudge shown once per 30 days, after the visitor
 * proved engagement (15s on page OR 40% scroll depth) on a content page or the homepage.
 */
const PreferredSourceNudge = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [done, setDone] = useState(false);

  const eligible = pathname === "/" || ELIGIBLE_PREFIXES.filter((p) => p !== "/").some((p) => pathname.startsWith(p));

  useEffect(() => {
    setVisible(false);
    setDone(false);
    if (!eligible || isPreferredSourceDone() || isNudgeSnoozed()) return;

    let timeReached = false;
    let scrollReached = false;
    let shown = false;

    const maybeShow = () => {
      if (shown || (!timeReached && !scrollReached)) return;
      shown = true;
      loadPreferredSourceLib();
      setVisible(true);
      trackEvent("preferred_source_impression", pathname, { variant: "nudge" });
    };

    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      if (window.scrollY / max >= MIN_SCROLL) {
        scrollReached = true;
        maybeShow();
      }
    };

    const timer = window.setTimeout(() => {
      timeReached = true;
      maybeShow();
    }, MIN_TIME_MS);

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [pathname, eligible]);

  if (!visible) return null;

  const dismiss = () => {
    snoozeNudge();
    trackEvent("preferred_source_dismiss", pathname, { variant: "nudge" });
    setVisible(false);
  };

  const accept = (e: React.MouseEvent) => {
    e.preventDefault();
    trackEvent("preferred_source_click", pathname, { variant: "nudge" });
    openPreferredSourceFlow();
    setDone(true);
    window.setTimeout(() => setVisible(false), 1800);
  };

  return (
    <div
      dir="rtl"
      role="dialog"
      aria-label="הוספת O-FIX כמקור מועדף בגוגל"
      className="fixed top-28 right-4 z-40 w-[min(323px,calc(100vw-6.5rem))] rounded-2xl border border-border/70 bg-background/95 backdrop-blur shadow-xl p-4 animate-in fade-in slide-in-from-top-4 duration-300"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="סגירה"
        className="absolute top-2 right-2 w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>

      <div className="flex items-start gap-3 pl-6">
        <span className="flex-shrink-0 w-9 h-9 rounded-xl bg-accent/10 flex items-center justify-center">
          <Star className="h-4.5 w-4.5 text-accent" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold text-foreground leading-snug">שיהיה לכם קל למצוא אותי</p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            סמנו את O-FIX כמקור מועדף בגוגל — כך התכנים שלי יופיעו לכם בולט יותר בחיפוש.
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        {done ? (
          <span className="inline-flex items-center gap-2 text-sm font-bold text-accent px-2 py-2">
            <Check className="h-4 w-4" aria-hidden="true" />
            תודה רבה!
          </span>
        ) : (
          <>
            <a
              href={PREFERRED_SOURCE_DEEPLINK}
              onClick={accept}
              target="_blank"
              rel="noopener noreferrer"
              data-cta-type="preferred_source"
              data-cta-location="nudge"
              className="flex-1 inline-flex items-center justify-center rounded-xl bg-accent px-4 py-2.5 text-sm font-bold text-accent-foreground hover:opacity-90 transition-opacity min-h-[44px]"
            >
              סימון בגוגל
            </a>
            <button
              type="button"
              onClick={dismiss}
              className="rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted transition-colors min-h-[44px]"
            >
              לא עכשיו
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PreferredSourceNudge;
